import { Helmet } from 'react-helmet-async';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Terms of Service - The Home Library Project</title>
        <meta name="description" content="Terms of Service for The Home Library Project" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-black mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Our terms and conditions
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-sm text-[#4a4a4a] mb-2"><strong>The Home Library Project</strong></p>
            <p className="text-sm text-[#4a4a4a] mb-2"><strong>Effective Date:</strong> November 24, 2025</p>
            <p className="text-sm text-[#4a4a4a] mb-8"><strong>Last Updated:</strong> November 24, 2025</p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Welcome to The Home Library Project. By accessing our website, making a donation, purchasing products, or using our services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              The Home Library Project is a Delaware nonprofit organization dedicated to promoting childhood literacy through book distribution and educational programs.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">2. Changes to Terms</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website with an updated "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the modified Terms. We encourage you to review these Terms periodically.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">3. Eligibility</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You must be at least 18 years old to make donations or purchases through our services. By using our services, you represent and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">4. Donations</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">4.1 Tax-Deductible Contributions</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              The Home Library Project is a tax-exempt organization under Section 501(c)(3) of the Internal Revenue Code. Donations made to us are tax-deductible to the extent permitted by law. We will provide donation receipts for your records.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">4.2 Donation Processing</h3>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>All donations are processed securely through Stripe, our third-party payment processor</li>
              <li>We accept major credit cards, debit cards, and other payment methods supported by Stripe</li>
              <li>You are responsible for ensuring that your payment information is accurate and current</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">4.3 One-Time and Recurring Donations</h3>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li><strong>One-Time Donations:</strong> Processed immediately upon submission</li>
              <li><strong>Recurring Donations:</strong> You authorize us to charge your payment method at the selected frequency (monthly, quarterly, annually) until you cancel</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">4.4 Canceling Recurring Donations</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              You may cancel recurring donations at any time by:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Logging into your account and updating your donation preferences</li>
              <li>Contacting us directly</li>
              <li>Following cancellation instructions in donation confirmation emails</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Cancellations take effect for the next billing cycle. We do not provide refunds for donations already processed.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">4.5 Use of Donations</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Donations support our mission to promote childhood literacy. While we honor donor designations when specified, we reserve the right to use donations where they are most needed unless restricted by law or donor agreement.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">4.6 Donation Refund Policy</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              As a nonprofit organization, donations are generally non-refundable. However, we will consider refund requests in the following circumstances:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Duplicate donations made in error</li>
              <li>Technical errors during the donation process</li>
              <li>Unauthorized transactions (subject to verification)</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Refund requests must be submitted within 30 days of the donation. To request a refund, contact us with your transaction details.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">5. Product Purchases</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.1 Book Boxes and Merchandise</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We offer book boxes and other products for sale to support our mission. All purchases are subject to these Terms.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.2 Pricing and Availability</h3>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>All prices are in U.S. dollars and are subject to change without notice</li>
              <li>We strive to display accurate pricing, but errors may occur. We reserve the right to correct pricing errors and cancel orders affected by such errors</li>
              <li>Product availability is subject to change. We will notify you if ordered items are unavailable</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.3 Order Processing and Fulfillment</h3>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Orders are processed after payment is confirmed through Stripe</li>
              <li>We will send order confirmation and shipping notification emails</li>
              <li>Estimated delivery times are provided at checkout but are not guaranteed</li>
              <li>Risk of loss passes to you upon delivery to the shipping carrier</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.4 Shipping</h3>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>We ship to addresses within the United States</li>
              <li>Shipping costs are calculated at checkout</li>
              <li>We are not responsible for delays caused by shipping carriers or circumstances beyond our control</li>
              <li>You are responsible for providing an accurate shipping address</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">5.5 Product Refunds and Returns</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              <strong>Book Boxes:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 text-base text-[#4a4a4a] space-y-2">
              <li>Unopened book boxes may be returned within 30 days of delivery for a full refund, minus shipping costs</li>
              <li>Opened book boxes may be returned within 30 days if items are damaged or defective</li>
              <li>Customer is responsible for return shipping costs unless the item is defective</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              <strong>Other Merchandise:</strong>
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Returns accepted within 30 days in original condition</li>
              <li>Non-refundable items will be clearly marked at the time of purchase</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              To initiate a return, contact us with your order number and reason for return.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">6. Payment Terms</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">6.1 Payment Processing</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              All payments are processed through Stripe. By providing payment information, you:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Authorize us to charge your payment method for the specified amount</li>
              <li>Represent that you are authorized to use the payment method provided</li>
              <li>Agree to pay all charges incurred</li>
              <li>Accept Stripe's Terms of Service and Privacy Policy</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">6.2 Payment Security</h3>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>We use industry-standard encryption to protect payment information</li>
              <li>We do not store complete credit card numbers on our servers</li>
              <li>Payment data is handled in compliance with PCI-DSS standards</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">6.3 Failed Payments</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              If a payment fails:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>We will attempt to contact you to update payment information</li>
              <li>For recurring donations, we may retry the charge</li>
              <li>Orders may be canceled if payment cannot be processed</li>
              <li>You remain responsible for any amounts owed</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">6.4 Chargebacks and Disputes</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              If you dispute a charge with your financial institution:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>We will respond to the chargeback according to our records</li>
              <li>Fraudulent chargebacks may result in suspension of services</li>
              <li>We encourage you to contact us directly to resolve any billing issues before initiating a chargeback</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">7. Account Registration</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">7.1 Account Creation</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              You may create an account to manage donations, view order history, and access certain features. You are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">7.2 Account Termination</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our discretion.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">8. Intellectual Property</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.1 Our Content</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              All content on our website, including text, graphics, logos, images, and software, is the property of The Home Library Project or its licensors and is protected by copyright and trademark laws.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.2 Limited License</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              We grant you a limited, non-exclusive, non-transferable license to access and use our website for personal, non-commercial purposes. You may not:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Reproduce, distribute, or create derivative works from our content without permission</li>
              <li>Use our content for commercial purposes</li>
              <li>Remove copyright or proprietary notices</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">8.3 Trademarks</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              "The Home Library Project" and associated logos are our trademarks. You may not use our trademarks without prior written consent.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">9. User Conduct</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Use our services for any unlawful purpose</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt our services or servers</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Collect information about other users</li>
              <li>Upload viruses, malware, or harmful code</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">10. Third-Party Services</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">10.1 Stripe</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Payment processing is provided by Stripe. Your use of Stripe's services is subject to Stripe's Terms of Service (https://stripe.com/legal) and Privacy Policy (https://stripe.com/privacy).
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">10.2 Other Third-Party Links</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Our website may contain links to third-party websites. We are not responsible for the content, terms, or practices of third-party sites. Use of third-party services is at your own risk.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">11. Disclaimers</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">11.1 Services "As Is"</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">11.2 No Guarantee of Availability</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We do not guarantee that our services will be uninterrupted, secure, or error-free. We may suspend or terminate services for maintenance or other reasons.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">11.3 Content Accuracy</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              While we strive to provide accurate information, we do not warrant the accuracy, completeness, or reliability of content on our website.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">12. Limitation of Liability</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              TO THE FULLEST EXTENT PERMITTED BY LAW, THE HOME LIBRARY PROJECT, ITS DIRECTORS, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>ANY LOSS OF PROFITS, REVENUE, DATA, OR USE</li>
              <li>ANY DAMAGES RESULTING FROM YOUR USE OF OR INABILITY TO USE OUR SERVICES</li>
              <li>ANY DAMAGES RESULTING FROM UNAUTHORIZED ACCESS TO YOUR DATA OR COMMUNICATIONS</li>
            </ul>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS OR YOUR USE OF OUR SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </p>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THESE LIMITATIONS MAY NOT APPLY TO YOU.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">13. Indemnification</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-4">
              You agree to indemnify, defend, and hold harmless The Home Library Project, its directors, officers, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
            </p>
            <ul className="list-disc pl-6 mb-6 text-base text-[#4a4a4a] space-y-2">
              <li>Your use of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your violation of any applicable laws or regulations</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">14. Dispute Resolution</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">14.1 Governing Law</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              These Terms are governed by the laws of the State of Delaware, without regard to its conflict of law provisions.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">14.2 Informal Resolution</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Before initiating formal proceedings, we encourage you to contact us to seek informal resolution of any disputes.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">14.3 Arbitration</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Any disputes arising from these Terms or your use of our services shall be resolved through binding arbitration in accordance with the American Arbitration Association's rules, except where prohibited by law. Arbitration shall take place in Delaware.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">14.4 Class Action Waiver</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You agree to resolve disputes on an individual basis and waive any right to bring claims as a class action, consolidated action, or representative action.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">14.5 Jurisdiction</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              If arbitration is not applicable, you agree to submit to the exclusive jurisdiction of the state and federal courts located in Delaware.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">15. Miscellaneous</h2>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">15.1 Entire Agreement</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and The Home Library Project regarding your use of our services.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">15.2 Severability</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">15.3 Waiver</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">15.4 Assignment</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">15.5 Force Majeure</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, natural disasters, war, terrorism, labor disputes, or government actions.
            </p>

            <h3 className="text-2xl font-bold text-[#2c5570] mt-6 mb-3">15.6 Survival</h3>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              Provisions that by their nature should survive termination of these Terms shall survive, including disclaimers, limitations of liability, indemnification, and dispute resolution.
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">16. Contact Information</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              If you have questions or concerns about these Terms, please contact us:
            </p>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              <strong>The Home Library Project</strong><br/>
              Email: info@thehomelibraryproject.org<br/>
              Address: 224 Hawkes Court, Hockessin, DE 19707
            </p>

            <h2 className="text-3xl font-bold text-[#2c5570] mt-8 mb-4">17. Acknowledgment</h2>
            <p className="text-base text-[#4a4a4a] leading-relaxed mb-6">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
            </p>

            <hr className="my-8 border-gray-300" />

            <p className="text-center text-base text-[#14919f] leading-relaxed font-semibold">
              Thank you for supporting The Home Library Project and our mission to promote childhood literacy!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
